import {isCopilot} from '@github-ui/assignees/copilot-user'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {readAssigneeFromFragment} from '@github-ui/item-picker-assignee-relay'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type-relay'
import {useCallback} from 'react'
import {graphql, readInlineData} from 'relay-runtime'

import type {useHandleTemplateChange$key} from './__generated__/useHandleTemplateChange.graphql'
import type {useHandleTemplateChangeIssueForm$key} from './__generated__/useHandleTemplateChangeIssueForm.graphql'
import type {useHandleTemplateChangeIssueTemplate$key} from './__generated__/useHandleTemplateChangeIssueTemplate.graphql'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
import {DisplayMode} from './utils/display-mode'
import {
  instanceOfBlankIssueData,
  instanceOfIssueFormData,
  instanceOfIssueTemplateData,
  type IssueCreatePayload,
  IssueCreationKind,
} from './utils/model'
import type {IssueCreateOptionConfig} from './utils/option-config'
import type {IssueCreateValueTypes} from './utils/template-args'
import {getTemplateUrl} from './utils/urls'

type useHandleTemplateChangeProps = {
  optionConfig: IssueCreateOptionConfig
  repository: RepositoryPickerRepository | undefined
  navigate: (url: string) => void
  setDisplayMode: (mode: DisplayMode) => void
}
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const UseHandleTemplateChangeIssueTemplateFragment = graphql`
  fragment useHandleTemplateChangeIssueTemplate on IssueTemplate @inline {
    # this field can go once we refactor the issues create dialog
    __id
    __typename
    name
    filename
    body
    title
    labels(first: 20, orderBy: {field: NAME, direction: ASC}) {
      edges {
        node {
          id
          color
          name
          nameHTML
          description
          url
        }
      }
    }
    assignees(first: 10) {
      edges {
        node {
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...AssigneePickerSharedAssignee
        }
      }
    }
    type {
      id
      name
      isEnabled
      description
      color
      pinnedFields {
        __typename
        ... on IssueFieldText {
          id
          name
          dataType
        }
        ... on IssueFieldSingleSelect {
          id
          name
          dataType
        }
        ... on IssueFieldDate {
          id
          name
          dataType
        }
        ... on IssueFieldNumber {
          id
          name
          dataType
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const UseHandleTemplateChangeIssueFormFragment = graphql`
  fragment useHandleTemplateChangeIssueForm on IssueForm @inline {
    # this field can go once we refactor the issues create dialog
    __id
    __typename
    name
    filename
    title
    # This is required to make the form work correctly
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...IssueFormElements_templateElements
    labels(first: 20, orderBy: {field: NAME, direction: ASC}) {
      edges {
        node {
          id
          color
          name
          nameHTML
          description
          url
        }
      }
    }
    assignees(first: 10) {
      edges {
        node {
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...AssigneePickerSharedAssignee
        }
      }
    }
    projects(first: 20) {
      edges {
        node {
          id
          title
          closed
          number
          url
          viewerCanUpdate
          hasReachedItemsLimit
          __typename
        }
      }
    }
    type {
      id
      name
      isEnabled
      description
      color
      pinnedFields {
        __typename
        ... on IssueFieldText {
          id
          name
          dataType
        }
        ... on IssueFieldSingleSelect {
          id
          name
          dataType
        }
        ... on IssueFieldDate {
          id
          name
          dataType
        }
        ... on IssueFieldNumber {
          id
          name
          dataType
        }
      }
    }
  }
`

export const getSelectedTemplate = (repository: useHandleTemplateChange$key): IssueCreatePayload | undefined => {
  // eslint-disable-next-line no-restricted-syntax
  const data = readInlineData(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment useHandleTemplateChange on Repository
      @inline
      @argumentDefinitions(filename: {type: "String!", defaultValue: ""}) {
        issueTemplate(filename: $filename) {
          ...useHandleTemplateChangeIssueTemplate
        }

        issueForm(filename: $filename) {
          ...useHandleTemplateChangeIssueForm
        }
      }
    `,
    repository,
  )
  let fetchedTemplate = undefined
  if (data.issueTemplate) {
    // eslint-disable-next-line no-restricted-syntax
    const templateData = readInlineData<useHandleTemplateChangeIssueTemplate$key>(
      UseHandleTemplateChangeIssueTemplateFragment,
      data.issueTemplate,
    )
    fetchedTemplate = {
      name: templateData.name,
      fileName: templateData.filename,
      kind: IssueCreationKind.IssueTemplate,
      data: templateData,
    }
  } else if (data.issueForm) {
    // eslint-disable-next-line no-restricted-syntax
    const templateData = readInlineData<useHandleTemplateChangeIssueForm$key>(
      UseHandleTemplateChangeIssueFormFragment,
      data.issueForm,
    )
    fetchedTemplate = {
      name: templateData.name,
      fileName: templateData.filename,
      kind: IssueCreationKind.IssueForm,
      data: templateData,
    }
  }
  return fetchedTemplate
}

export const useHandleTemplateChange = ({
  optionConfig,
  repository,
  navigate,
  setDisplayMode,
}: useHandleTemplateChangeProps) => {
  const {
    setTemplate,
    reinitTitle,
    reinitBody,
    reinitAssignees,
    reinitLabels,
    reinitProjects,
    reinitMilestone,
    reinitIssueType,
    setIsCrossRepositoryDialogOpen,
  } = useIssueCreateDataContext()

  return useCallback(
    (selectedTemplate?: IssueCreatePayload, enforcedInitialValues?: IssueCreateValueTypes, enforceNavigate = false) => {
      setTemplate(selectedTemplate)

      let newTitle = ''
      if (enforcedInitialValues?.discussion) {
        newTitle = enforcedInitialValues.discussion.title
      } else if (enforcedInitialValues?.title) {
        newTitle = enforcedInitialValues.title || ''
      } else {
        const templateTitle = selectedTemplate?.data.title || ''
        let userInputTitle = ''
        if (enforcedInitialValues?.appendTitleToTemplate) {
          userInputTitle = ` ${enforcedInitialValues.appendTitleToTemplate}`
        }
        newTitle = [templateTitle, userInputTitle].join('').trimStart()
      }

      reinitTitle(newTitle)

      let newBody = ''
      if (enforcedInitialValues?.discussion) {
        newBody = enforcedInitialValues.discussion.formattedBody || ''
      } else if (enforcedInitialValues?.body) {
        newBody = enforcedInitialValues.body || ''
      } else if (
        selectedTemplate &&
        (instanceOfIssueTemplateData(selectedTemplate.data) || instanceOfBlankIssueData(selectedTemplate.data))
      ) {
        newBody = selectedTemplate.data.body || ''
      }
      reinitBody(newBody)

      let templateLabels = []
      if (enforcedInitialValues?.discussion) {
        templateLabels = enforcedInitialValues.discussion?.labels || []
      } else if (enforcedInitialValues?.labels) {
        templateLabels = enforcedInitialValues.labels
      } else {
        templateLabels = selectedTemplate?.data.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])) || []
      }

      reinitLabels(templateLabels)

      const templateAssignees =
        enforcedInitialValues?.assignees ??
        ((selectedTemplate?.data.assignees?.edges || []).flatMap(e =>
          e?.node ? [readAssigneeFromFragment(e?.node)] : [],
        ) ||
          [])
      reinitAssignees(templateAssignees)

      const hasCopilotInTemplate = templateAssignees.some(assignee => isCopilot(assignee.login))
      if (hasCopilotInTemplate) {
        setIsCrossRepositoryDialogOpen(true)
      }

      if (enforcedInitialValues?.projects) {
        reinitProjects(enforcedInitialValues.projects)
      } else if (selectedTemplate && instanceOfIssueFormData(selectedTemplate?.data)) {
        const templateProjects = selectedTemplate.data.projects?.edges?.flatMap(e => (e?.node ? [e.node] : [])) || []
        reinitProjects(templateProjects)
      }

      if (enforcedInitialValues?.milestone) {
        reinitMilestone(enforcedInitialValues?.milestone)
      }

      let templateType: IssueTypePickerIssueType | null = enforcedInitialValues?.type ?? null
      if (
        selectedTemplate &&
        !templateType &&
        (instanceOfIssueFormData(selectedTemplate.data) || instanceOfIssueTemplateData(selectedTemplate.data)) &&
        selectedTemplate.data.type !== undefined
      ) {
        templateType = selectedTemplate.data.type || null
      }
      reinitIssueType(templateType ?? null)

      if (!enforcedInitialValues || enforceNavigate) {
        if (optionConfig.navigateToFullScreenOnTemplateChoice && repository && selectedTemplate) {
          navigate(getTemplateUrl(repository.nameWithOwner, selectedTemplate.fileName))
        } else {
          setDisplayMode(DisplayMode.IssueCreation)
        }
      }
    },
    [
      setTemplate,
      reinitTitle,
      reinitBody,
      reinitLabels,
      reinitAssignees,
      reinitIssueType,
      reinitProjects,
      reinitMilestone,
      optionConfig.navigateToFullScreenOnTemplateChoice,
      repository,
      navigate,
      setDisplayMode,
      setIsCrossRepositoryDialogOpen,
    ],
  )
}
